// Phase 26F.3 (closer) — Outlet-mode multi-select bulk retailer transfer.
//
// Consumes the shared `BulkActionGrid` from 26E. Selection persists across
// pages (Set-based). Bulk action `Transfer` prompts for target beat and/or
// target distributor (at least one required, validated client + server side),
// then POSTs `/api/retailers/bulk-transfer` and shows a toast whose counts
// are READ FROM THE RESPONSE BODY (Array.isArray-guarded) — never from the
// selection state — per the hard 26E.2 lesson on silent-success bugs.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import BulkActionGrid from "@/components/BulkActionGrid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";

export default function RetailerTransferGrid() {
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [beats, setBeats] = useState([]);

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptRows, setPromptRows] = useState([]);   // selected rows passed by BulkActionGrid
  const [targetBeatId, setTargetBeatId] = useState("");
  const [targetDistributorId, setTargetDistributorId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);

  useEffect(() => {
    api.get("/zones").then(r => setZones((r.data.items || r.data || []).map(z => ({ value: z.id, label: `${z.code} · ${z.name}` }))));
    api.get("/masters/sub-zones", { params: { page_size: 200 } })
        .then(r => setSubZones((r.data.items || []).map(s => ({ value: s.id, label: s.name })))).catch(() => {});
    api.get("/distributors", { params: { page_size: 2000 } })
        .then(r => setDistributors((r.data.items || []).map(d => ({ value: d.id, label: `${d.code || ""} · ${d.name}` }))));
    // page_size 500 used to leave ~1300 of the system's 1800+ beats out of
    // this name-lookup map — any outlet whose beat fell past the first 500
    // showed a raw truncated beat_id in the grid instead of its code/name.
    // 2000 is the backend's actual max (routes_masters_read.py's
    // list_beats caps page_size at le=2000); bump this again if the beat
    // count ever grows past that.
    api.get("/beats", { params: { page_size: 2000 } })
        .then(r => setBeats((r.data.items || []).map(b => ({ value: b.id, label: `${b.code || ""} · ${b.name}`, zone_id: b.zone_id, distributor_id: b.distributor_id }))));
  }, []);

  const nameById = useMemo(() => {
    const m = new Map();
    for (const z of zones) m.set(z.value, z.label);
    for (const s of subZones) m.set(s.value, s.label);
    for (const d of distributors) m.set(d.value, d.label);
    for (const b of beats) m.set(b.value, b.label);
    return m;
  }, [zones, subZones, distributors, beats]);

  const filters = useMemo(() => [
    { key: "q", label: "Search", type: "text",
      placeholder: "Outlet code or name…" },
    { key: "zone_id", label: "Zone", type: "select", options: zones,
      placeholder: "All zones" },
    { key: "distributor_id", label: "Distributor", type: "select", options: distributors,
      placeholder: "All distributors" },
    { key: "beat_id", label: "Beat", type: "select", options: beats,
      placeholder: "All beats" },
  ], [zones, distributors, beats]);

  const columns = useMemo(() => [
    { key: "code", label: "Outlet Code", width: "12ch" },
    { key: "name", label: "Outlet Name" },
    { key: "beat_id", label: "Beat", render: (row) => nameById.get(row.beat_id) || (row.beat_id ? row.beat_id.slice(0, 8) + "…" : "—") },
    { key: "distributor_id", label: "Distributor", render: (row) => nameById.get(row.distributor_id) || (row.distributor_id ? row.distributor_id.slice(0, 8) + "…" : "—") },
    { key: "zone_id", label: "Zone", render: (row) => nameById.get(row.zone_id) || row.zone_id || "—" },
  ], [nameById]);

  const onLoad = useCallback(async (query) => {
    // Strip empty filter values AND the sort_* keys /retailers doesn't accept.
    const params = { page: query.page, page_size: query.page_size };
    for (const k of ["q", "zone_id", "distributor_id", "beat_id"]) {
      if (query[k]) params[k] = query[k];
    }
    const { data } = await api.get("/retailers", { params });
    return { rows: data.items || [], total: data.total || 0 };
  }, []);

  const openPrompt = (rows) => {
    setPromptRows(rows);
    setTargetBeatId("");
    setTargetDistributorId("");
    setPromptOpen(true);
  };

  const execute = async () => {
    if (!targetBeatId && !targetDistributorId) {
      toast.error("Pick at least one target (beat or distributor)");
      return;
    }
    setSubmitting(true);
    try {
      const body = { outlet_ids: promptRows.map(r => r.id) };
      if (targetBeatId) body.target_beat_id = targetBeatId;
      if (targetDistributorId) body.target_distributor_id = targetDistributorId;
      const { data } = await api.post("/retailers/bulk-transfer", body);

      // Read counts from the RESPONSE BODY (per 26E.2 lesson) — never from
      // the selection size. Array.isArray-guard against a malformed response.
      const appliedList = Array.isArray(data?.applied) ? data.applied : [];
      const skippedList = Array.isArray(data?.skipped) ? data.skipped : [];
      const totalReq = typeof data?.total_requested === "number"
        ? data.total_requested : promptRows.length;

      if (appliedList.length > 0) {
        toast.success(`Applied ${appliedList.length} · Skipped ${skippedList.length} (of ${totalReq})`);
      } else if (skippedList.length > 0) {
        // Enumerate the first skip reason to help the operator.
        const reasons = [...new Set(skippedList.map(s => s?.status || s?.reason).filter(Boolean))];
        toast.warning(`No transfers applied · ${skippedList.length} skipped (${reasons.join(", ") || "unknown reason"})`);
      } else {
        toast.info("No changes");
      }
      setPromptOpen(false);
      setGridReloadKey(k => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const bulkActions = useMemo(() => [{
    key: "transfer",
    label: "Transfer",
    icon: ArrowRightLeft,
    onExecute: (rows) => openPrompt(rows),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }], []);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="retailer-transfer-grid-page">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <ArrowRightLeft className="h-6 w-6 text-[#6D2C91]" /> Bulk Retailer Transfer · Outlet Mode
        </h1>
        <p className="text-xs text-neutral-500 mt-0.5">
          Multi-select outlets across pages, then transfer to a target beat and/or distributor.
          Every applied row is audit-logged. Skipped rows show the reason.
        </p>
      </div>

      <BulkActionGrid
        key={gridReloadKey}
        testid="retailer-transfer-grid"
        filters={filters}
        columns={columns}
        onLoad={onLoad}
        bulkActions={bulkActions}
        rowKey="id"
      />

      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent data-testid="retailer-transfer-prompt">
          <DialogHeader>
            <DialogTitle>Transfer {promptRows.length} outlet(s)</DialogTitle>
            <DialogDescription>
              Pick at least one target. Fields left blank stay unchanged for each selected outlet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Target beat (optional)</Label>
              <SearchableSelect
                data-testid="prompt-target-beat"
                value={targetBeatId} onValueChange={setTargetBeatId}
                options={beats}
                placeholder="— pick a beat —"
              />
            </div>
            <div>
              <Label>Target distributor (optional)</Label>
              <SearchableSelect
                data-testid="prompt-target-distributor"
                value={targetDistributorId} onValueChange={setTargetDistributorId}
                options={distributors}
                placeholder="— pick a distributor —"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromptOpen(false)}
                    data-testid="prompt-cancel">Cancel</Button>
            <Button onClick={execute}
                    disabled={submitting || (!targetBeatId && !targetDistributorId)}
                    className="tulsi-gradient text-white"
                    data-testid="prompt-execute">
              {submitting ? "Transferring…" : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
